"use client";

import { useLang } from "@/contexts/language-context";
import Link from "next/link";
import { getFeaturesByVertical } from "@/lib/marketing/features-data";
import AppStoreBadges from "./AppStoreBadges";
import MarketingPrivacyChoices from "./MarketingPrivacyChoices";

const paymentLogos = [
  { src: "/logo-visa.png",       alt: "Visa",           h: "h-5" },
  { src: "/logo-mastercard.png", alt: "Mastercard",     h: "h-6" },
  { src: "/logo-amex.png",       alt: "Amex",           h: "h-5" },
  { src: "/logo-discover.png",   alt: "Discover",       h: "h-5" },
  { src: "/logo-applepay.png",   alt: "Apple Pay",      h: "h-5" },
  { src: "/logo-googlepay.png",  alt: "Google Pay",     h: "h-5" },
  { src: "/logo-north.png",      alt: "North",          h: "h-5" },
];

function InstagramIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.28a8.16 8.16 0 0 0 4.78 1.52V7.35a4.85 4.85 0 0 1-1.01-.66z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

// Features for the footer columns (slug + label keys).
// Order tuned for marketing prominence — not alphabetical.
// Los 13 de restaurant están listados: el footer es el único enlace sitewide que
// reciben las páginas de feature, y las 4 que faltaban aquí eran las 4 sin enlace
// interno desde la home (rush-hour-pos era la única que GSC marcaba como
// "rastreada, sin indexar").
const RESTAURANT_FOOTER_FEATURES = [
  "zero-processing-fees",
  "live-menu-boards",
  "instant-website",
  "rush-hour-pos",
  "kitchen-display-system",
  "self-order-kiosk",
  "online-ordering",
  "delivery",
  "employee-time-clock",
  "sms-customers-staff",
  "inventory-cogs",
  "loyalty-crm",
  "works-offline",
];

const NIGHTLIFE_FOOTER_FEATURES = [
  "event-ticketing",
  "qr-door-check-in",
  "live-bar-tabs",
  "vip-table-map",
  "promoter-portal",
  "venue-microsite",
];

export default function MarketingFooter() {
  const { t } = useLang();

  const allRestaurant = getFeaturesByVertical("restaurant");
  const allNightlife = getFeaturesByVertical("nightlife");
  const restaurantTop = RESTAURANT_FOOTER_FEATURES
    .map((slug) => allRestaurant.find((f) => f.slug === slug))
    .filter((f): f is NonNullable<typeof f> => Boolean(f));
  const nightlifeTop = NIGHTLIFE_FOOTER_FEATURES
    .map((slug) => allNightlife.find((f) => f.slug === slug))
    .filter((f): f is NonNullable<typeof f> => Boolean(f));

  return (
    <footer className="bg-white border-t border-black/5">
      {/* Main grid */}
      <div className="max-w-[1280px] mx-auto px-6 pt-16 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8">

          {/* Col 1 — Brand (4 cols) */}
          <div className="col-span-2 md:col-span-4">
            <Link href="/" className="inline-block">
              <img src="/redy-logo.png" alt="Redy" className="h-8 w-auto mb-5" />
            </Link>
            <p className="text-[14px] font-medium text-redy-secondary leading-relaxed mb-6 max-w-[260px]">
              {t(
                "One platform for restaurants, food trucks, bars and nightclubs. Free software plan with Redy payments. Dual pricing built in. Month to month.",
                "Una plataforma para restaurantes, food trucks, bares y nightclubs. Plan de software gratis con pagos de Redy. Dual pricing incluido. Mes a mes."
              )}
            </p>
            <div className="text-[13px] font-medium text-redy-secondary mb-1">
              5900 Balcones Dr, Ste 100, Austin, TX 78731
            </div>
            <a
              href="tel:+12345425633"
              className="block text-[13px] font-medium text-redy-secondary hover:text-redy-charcoal transition-colors mb-1"
            >
              +1 (234) KICKOFF (+1 234-542-5633)
            </a>
            <a
              href="mailto:hello@useredy.com"
              className="text-[13px] font-bold text-redy-charcoal hover:text-redy-orange transition-colors"
            >
              hello@useredy.com
            </a>

            {/* Social icons */}
            <div className="flex items-center gap-4 mt-6">
              {[
                { href: "https://www.instagram.com/redy_POS", icon: <InstagramIcon />, label: "Instagram" },
                { href: "https://www.tiktok.com/@redypos",  icon: <TikTokIcon />,    label: "TikTok"    },
                { href: "https://x.com/redypos",        icon: <XIcon />,         label: "X"         },
                { href: "https://www.linkedin.com/company/redy-pos/", icon: <LinkedInIcon />, label: "LinkedIn" },
              ].map(({ href, icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 bg-[#FAFAFA] border border-black/5 rounded-full flex items-center justify-center text-redy-charcoal/50 hover:text-redy-charcoal hover:border-black/15 hover:bg-white transition-all"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Col 2 — Solutions (2 cols) */}
          <div className="md:col-span-2">
            <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal mb-5">
              {t("Solutions", "Soluciones")}
            </div>
            <ul className="space-y-2.5">
              {[
                { href: "/solution/restaurants",     en: "Restaurants (Dine-in)", es: "Restaurantes (Dine-in)" },
                { href: "/solution/food-trucks-qsr", en: "Food Trucks & QSR",     es: "Food Trucks y QSR"      },
                { href: "/solution/nightlife",       en: "Bars & Nightclubs",     es: "Bares y Nightclubs"     },
                { href: "/pricing",              en: "Pricing",            es: "Precios"             },
                { href: "/hardware",             en: "Hardware",           es: "Hardware"            },
                { href: "/features",             en: "All Features",       es: "Todas las Features"  },
              ].map((l) => (
                <li key={l.href}>
                  <Link
                    href={l.href}
                    className="text-[14px] font-medium text-redy-secondary hover:text-redy-charcoal transition-colors"
                  >
                    {t(l.en, l.es)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Restaurant features (3 cols) */}
          <div className="md:col-span-3">
            <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal mb-5">
              {t("Restaurant Features", "Features Restaurant")}
            </div>
            <ul className="space-y-2.5">
              {restaurantTop.map((f) => (
                <li key={f.slug}>
                  <Link
                    href={`/features/${f.slug}`}
                    className="text-[14px] font-medium text-redy-secondary hover:text-redy-charcoal transition-colors"
                  >
                    {t(f.card.title.en, f.card.title.es)}
                  </Link>
                </li>
              ))}
              <li className="pt-1.5">
                <Link
                  href="/features"
                  className="inline-flex items-center gap-1 text-[12.5px] font-bold text-redy-charcoal hover:text-redy-orange transition-colors"
                >
                  {t("All features →", "Todas las features →")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4 — Nightlife features (3 cols) */}
          <div className="md:col-span-3">
            <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal mb-5 flex items-center gap-2">
              {t("Nightlife Features", "Features Nightlife")}
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: "#7C3AED" }}
              />
            </div>
            <ul className="space-y-2.5">
              {nightlifeTop.map((f) => (
                <li key={f.slug}>
                  <Link
                    href={`/features/${f.slug}`}
                    className="text-[14px] font-medium text-redy-secondary hover:text-redy-charcoal transition-colors"
                  >
                    {t(f.card.title.en, f.card.title.es)}
                  </Link>
                </li>
              ))}
              <li className="pt-1.5">
                <Link
                  href="/solution/nightlife"
                  className="inline-flex items-center gap-1 text-[12.5px] font-bold hover:opacity-80 transition-opacity"
                  style={{ color: "#7C3AED" }}
                >
                  {t("Explore Nightlife →", "Explorar Nightlife →")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Secondary row — Company + Get started */}
        <div className="mt-14 pt-10 border-t border-black/5 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Get started CTA */}
          <div className="md:col-span-5">
            <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal mb-3">
              {t("Get started", "Comenzar")}
            </div>
            <p className="text-[14px] font-medium text-redy-secondary leading-relaxed mb-4 max-w-[400px]">
              {t(
                "Free software plan with Redy payments. Dual pricing built in. Month to month, no long-term contract. Card payments go live once your payments account is approved.",
                "Plan de software gratis con pagos de Redy. Dual pricing incluido. Mes a mes, sin contrato a largo plazo. Los cobros con tarjeta se activan cuando se aprueba tu cuenta de pagos."
              )}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                href="/signup"
                className="inline-flex items-center gap-2 bg-redy-charcoal text-white font-bold px-6 py-3 rounded-full hover:bg-black transition-all text-[14px] active:scale-95"
              >
                {t("Create account →", "Crear cuenta →")}
              </Link>
              <Link
                href="/login"
                className="inline-flex items-center text-[14px] font-bold text-redy-charcoal px-5 py-3 rounded-full border border-black/10 hover:bg-[#FAFAFA] transition-all"
              >
                {t("Log in", "Iniciar sesión")}
              </Link>
            </div>

            {/* Native app — Redy POS on both stores */}
            <div className="mt-7">
              <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal mb-3">
                {t("Get the app", "Descarga la app")}
              </div>
              <AppStoreBadges height={40} />
            </div>
          </div>

          {/* Company links */}
          <div className="md:col-span-3 md:col-start-7">
            <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal mb-3">
              {t("Company", "Empresa")}
            </div>
            <ul className="space-y-2.5">
              {[
                { href: "/#how-it-works", en: "How It Works", es: "Cómo Funciona" },
                { href: "/#dual-pricing", en: "Dual Pricing", es: "Dual Pricing" },
                { href: "/blog", en: "Blog", es: "Blog" },
                { href: "/order", en: "Order Food", es: "Pedir comida" },
                { href: "/help", en: "Help", es: "Ayuda" },
                { href: "/contact", en: "Contact", es: "Contacto" },
              ].map((l) => (
                <li key={l.href}>
                  <Link
                    href={l.href}
                    className="text-[14px] font-medium text-redy-secondary hover:text-redy-charcoal transition-colors"
                  >
                    {t(l.en, l.es)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-3">
            <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal mb-3">
              {t("Legal", "Legal")}
            </div>
            <ul className="space-y-2.5">
              {[
                { href: "/privacy",            en: "Privacy Policy",     es: "Privacidad"          },
                { href: "/terms",              en: "Terms of Service",   es: "Términos"             },
                { href: "/refund",             en: "Refund & Cancellation", es: "Reembolsos"        },
                { href: "/sms-terms",          en: "SMS Terms",          es: "Términos SMS"         },
                { href: "/merchant-agreement", en: "Merchant Agreement", es: "Acuerdo de Merchant"  },
              ].map((l) => (
                <li key={l.href}>
                  <Link
                    href={l.href}
                    className="text-[14px] font-medium text-redy-secondary hover:text-redy-charcoal transition-colors"
                  >
                    {t(l.en, l.es)}
                  </Link>
                </li>
              ))}
              <li>
                <MarketingPrivacyChoices className="text-[14px] font-medium text-redy-secondary hover:text-redy-charcoal transition-colors text-left" />
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-black/5">
        <div className="max-w-[1280px] mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[12px] font-medium text-redy-charcoal/30">
            © {new Date().getFullYear()} Kickoff Business Solutions LLC DBA Redy · Austin, TX ·{" "}
            <Link href="/privacy" className="hover:text-redy-charcoal/60 transition-colors">
              {t("Privacy", "Privacidad")}
            </Link>
            {" · "}
            <Link href="/terms" className="hover:text-redy-charcoal/60 transition-colors">
              {t("Terms", "Términos")}
            </Link>
            {" · "}
            <MarketingPrivacyChoices className="hover:text-redy-charcoal/60 transition-colors" />
          </p>

          {/* Payment logos */}
          <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-end">
            {paymentLogos.map((logo) => (
              <img
                key={logo.alt}
                src={logo.src}
                alt={logo.alt}
                className={`${logo.h} w-auto object-contain`}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
